import { useEffect, useRef, useState } from 'react';
import { Volume2, VolumeX } from 'lucide-react';
import { ENV } from '../../config/env';
import { useI18n } from '../../hooks/useI18n';
import Button from '../ui/Button';

interface HeroVideoCardProps {
  videoSrc?: string;
  poster?: string;
  youtubeVideoId?: string;
  title?: string;
  className?: string;
  aspectClassName?: string;
  controls?: boolean;
}

export default function HeroVideoCard({
  videoSrc = ENV.HERO_VIDEO_URL,
  poster,
  youtubeVideoId = ENV.YOUTUBE_VIDEO_ID,
  title = 'TECNICELU S.A. Video',
  className = '',
  aspectClassName = 'aspect-video',
  controls = false,
}: HeroVideoCardProps) {
  const { t } = useI18n();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [muted, setMuted] = useState(true);
  const [videoReady, setVideoReady] = useState(false);
  const cardClassName = `relative ${aspectClassName} overflow-hidden rounded-2xl bg-raised ${className}`;

  useEffect(() => {
    let cancelled = false;
    const ready = () => {
      if (!cancelled) setVideoReady(true);
    };
    const idle = (window as { requestIdleCallback?: (cb: () => void, options?: { timeout: number }) => number; cancelIdleCallback?: (id: number) => void }).requestIdleCallback;
    let handle: number;
    if (idle) handle = idle(ready, { timeout: 2500 });
    else handle = window.setTimeout(ready, 500);
    return () => {
      cancelled = true;
      if (idle) window.cancelIdleCallback(handle);
      else window.clearTimeout(handle);
    };
  }, []);

  if (videoSrc) {
    const toggleAudio = () => {
      setMuted((current) => {
        const next = !current;
        if (videoRef.current) {
          videoRef.current.muted = next;
          if (!next) void videoRef.current.play().catch(() => undefined);
        }
        return next;
      });
    };

    return (
      <div className={cardClassName}>
        <video
          ref={videoRef}
          className="h-full w-full object-cover"
          src={videoReady ? videoSrc : undefined}
          poster={poster}
          controls={controls}
          autoPlay={!controls}
          muted={muted}
          loop
          playsInline
          preload={videoReady ? 'auto' : 'none'}
          aria-label={title}
        />
        {!controls && (
          <Button
            type="button"
            variant="glass-secondary"
            size="sm"
            className="absolute top-2 right-2 h-10 w-10 rounded-full border border-glass-line/30 bg-canvas/80 text-ink shadow-lg backdrop-blur-md hover:bg-canvas/95 flex items-center justify-center"
            onClick={toggleAudio}
            aria-label={muted ? t('video_unmute') : t('video_mute')}
            title={muted ? t('video_unmute') : t('video_mute')}
          >
            {muted ? <VolumeX className="h-5 w-5" fill="currentColor" aria-hidden="true" /> : <Volume2 className="h-5 w-5" fill="currentColor" aria-hidden="true" />}
          </Button>
        )}
      </div>
    );
  }

  if (!youtubeVideoId) return null;

  return (
    <div className={cardClassName}>
      <iframe
        className="h-full w-full"
        src={`https://www.youtube-nocookie.com/embed/${youtubeVideoId}?autoplay=1&mute=1&rel=0&controls=${controls ? '1' : '0'}&modestbranding=1&showinfo=0&fs=0&iv_load_policy=3&disablekb=1&playsinline=1&cc_load_policy=0`}
        title={title}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </div>
  );
}
